// Arabic Lab progress: pure helpers shared by the app and tests (no imports). The account keeps one review record per
// practised item and skill (api/src/arabicProgress.ts); these turn the progress older versions kept in a browser into
// records for the one-time import, and order practice by what is due.
export type ProgressSkill='read'|'listen'|'build'|'write'|'shadow'|'quiz'|'recognise'
export type ProgressRecord={item_key:string;skill:ProgressSkill;kind:string;course_notebook_id:string;label:string;seen:number;correct:number;hard:number;again:number;last_rating:string;interval_days:number;ease_factor:number;next_review_at:string|null;first_at:string;last_at:string|null}
export type ImportRecord={item_key:string;skill:ProgressSkill;kind:string;label:string;seen:number;correct:number;last_at?:string}

/** The browser stores older versions kept (left in place after import, as a safety net). */
export const LOCAL_STORES={
 course:'dawwin-arabic-course-history-v1',
 daily:'dawwin-arabic-daily-five-review-v1',
 alphabet:'dawwin-arabic-alphabet-known-v1',
 learning:'dawwin-arabic-lab-learning-v1',
 words:'dawwin-arabic-lab-preview-progress-v1',
 checkpoints:'dawwin-arabic-course-checkpoints-v1',
} as const

const n=(v:unknown)=>typeof v==='number'&&Number.isFinite(v)&&v>0?Math.round(v):0
const obj=(raw:string|null):Record<string,any>=>{try{const v=JSON.parse(raw||'{}');return v&&typeof v==='object'&&!Array.isArray(v)?v:{}}catch{return{}}}

/** Reads every older store through `read` (e.g. localStorage.getItem) and returns the records to import. */
export function collectLocalProgress(read:(key:string)=>string|null):ImportRecord[]{
 const out:ImportRecord[]=[]
 for(const [id,h] of Object.entries(obj(read(LOCAL_STORES.course))))
  if(n(h?.seen))out.push({item_key:`course:${id}`,skill:'read',kind:'course',label:'',seen:n(h.seen),correct:Math.min(n(h.right),n(h.seen)),...(typeof h.last==='string'&&h.last?{last_at:h.last}:{})})
 for(const [key,h] of Object.entries(obj(read(LOCAL_STORES.daily)))){
  const seen=n(h?.mistakes)+n(h?.correct)
  if(seen)out.push({item_key:`daily:${key}`,skill:'quiz',kind:'daily',label:key.split('|')[0]||'',seen,correct:n(h.correct)})
 }
 try{
  const letters=JSON.parse(read(LOCAL_STORES.alphabet)||'[]')
  if(Array.isArray(letters))for(const l of new Set(letters.filter((x:unknown)=>typeof x==='string'&&x)))out.push({item_key:`letter:${l}`,skill:'recognise',kind:'letter',label:l as string,seen:1,correct:1})
 }catch{/* unreadable: nothing to import */}
 for(const [key,h] of Object.entries(obj(read(LOCAL_STORES.learning)))){
  const seen=n(h?.correct)+n(h?.wrong)
  if(seen)out.push({item_key:`learning:${key}`,skill:'quiz',kind:'learning',label:'',seen,correct:n(h.correct)})
 }
 for(const [key,h] of Object.entries(obj(read(LOCAL_STORES.words))))
  if(n(h?.seen))out.push({item_key:`word:${key}`,skill:'read',kind:'word',label:'',seen:n(h.seen),correct:Math.min(n(h.correct),n(h.seen))})
 return out.filter(r=>r.item_key.length<=300)
}

export const isDueRecord=(r:ProgressRecord|undefined,now=Date.now())=>!!r&&(!r.next_review_at||Date.parse(r.next_review_at)<=now)

/**
 * Practice order from the account's schedule: items due for this skill (most overdue first), then items never
 * practised in this skill (in lesson order), then the rest by when they next fall due.
 */
export function progressOrder<T extends {id:string}>(items:T[],records:Map<string,ProgressRecord>,skill:ProgressSkill,now=Date.now(),key=(i:T)=>`course:${i.id}`):T[]{
 const at=(i:T)=>records.get(`${key(i)}|${skill}`)
 const due=items.filter(i=>isDueRecord(at(i),now)).sort((a,b)=>Date.parse(at(a)!.next_review_at||'0')-Date.parse(at(b)!.next_review_at||'0'))
 const fresh=items.filter(i=>!at(i))
 const later=items.filter(i=>at(i)&&!isDueRecord(at(i),now)).sort((a,b)=>Date.parse(at(a)!.next_review_at!)-Date.parse(at(b)!.next_review_at!))
 return[...due,...fresh,...later]
}
export const recordMap=(records:ProgressRecord[])=>new Map(records.map(r=>[`${r.item_key}|${r.skill}`,r]))

export type CheckpointImport={section:string;best:number;last:number;total:number;date:string}
const frac=(v:unknown)=>typeof v==='number'&&Number.isFinite(v)?Math.max(0,Math.min(1,v)):0
/** The checkpoint results older versions kept in this browser (by section; the course was not noted). */
export function collectLocalCheckpoints(read:(key:string)=>string|null):CheckpointImport[]{
 return Object.entries(obj(read(LOCAL_STORES.checkpoints))).map(([section,r])=>({section,best:frac(r?.best),last:frac(r?.last),total:n(r?.total),date:typeof r?.date==='string'?r.date:''}))
  .filter(r=>r.section&&r.total>0)
}

// Mastery. Each record (an item in one skill) is weak when last missed or missed in two answers of five or more;
// mastered once the schedule has stretched to three weeks or more, which takes about five right answers in a row
// across a month; otherwise learning. "Due" is separate: any of these can be due.
export type Mastery='mastered'|'learning'|'weak'
export const MASTERED_DAYS=21
export function masteryOf(r:ProgressRecord):Mastery{
 if(r.last_rating==='again'||(r.seen>=2&&r.again/r.seen>=.4))return'weak'
 if(r.interval_days>=MASTERED_DAYS)return'mastered'
 return'learning'
}
/** An item practised in several skills: weak if weak in any, mastered only when mastered in all. */
export function itemMastery(records:ProgressRecord[]):Mastery{
 const m=records.map(masteryOf)
 return m.includes('weak')?'weak':m.every(x=>x==='mastered')?'mastered':'learning'
}
export type MasteryCounts={items:number;due:number;mastered:number;learning:number;weak:number}
/** Counts by item: records are grouped by item_key (one item, however many skills). */
export function masteryCounts(records:ProgressRecord[],now=Date.now()):MasteryCounts{
 const byItem=new Map<string,ProgressRecord[]>()
 for(const r of records)byItem.set(r.item_key,[...(byItem.get(r.item_key)||[]),r])
 const c:MasteryCounts={items:0,due:0,mastered:0,learning:0,weak:0}
 for(const rs of byItem.values()){c.items++;c[itemMastery(rs)]++;if(rs.some(r=>isDueRecord(r,now)))c.due++}
 return c
}
/** Counts per group (e.g. per source or per skill); records with no group are left out. */
export function masteryBy(records:ProgressRecord[],group:(r:ProgressRecord)=>string,now=Date.now()){
 const groups=new Map<string,ProgressRecord[]>()
 for(const r of records){const g=group(r);if(g)groups.set(g,[...(groups.get(g)||[]),r])}
 return new Map([...groups].map(([g,rs])=>[g,masteryCounts(rs,now)]))
}
/** Where an item comes from, for the dashboard: its course, or the Lab section that keeps it. */
export function sourceOf(r:ProgressRecord){
 const prefix=r.item_key.split(':')[0]
 return prefix==='course'?`course:${r.course_notebook_id}`:prefix==='letter'||prefix==='learning'?'letters':prefix==='word'?'words':prefix==='daily'?'daily':prefix==='grammar'?'grammar':prefix==='qaida'?'qaida':''
}
/** A letter's state from its alphabet and reading-lesson records; undefined when never practised. */
export function letterMastery(records:ProgressRecord[],letterId:string):Mastery|undefined{
 const rs=records.filter(r=>r.item_key===`letter:${letterId}`||r.item_key===`learning:${letterId}`||r.item_key===`qaida:letter:${letterId}`)
 return rs.length?itemMastery(rs):undefined
}

export type ActivityDay={day:string;answers:number;right:number}
const localDay=(d:Date)=>[d.getFullYear(),String(d.getMonth()+1).padStart(2,'0'),String(d.getDate()).padStart(2,'0')].join('-')
/** The last `days` days ending today (local), oldest first, with zero for days without answers. */
export function calendarDays(activity:ActivityDay[],days=30,today=new Date()):ActivityDay[]{
 const by=new Map(activity.map(a=>[a.day,a]))
 return Array.from({length:days},(_,i)=>{const k=localDay(new Date(today.getFullYear(),today.getMonth(),today.getDate()-(days-1-i)));return by.get(k)||{day:k,answers:0,right:0}})
}
/** Days in a row with answers, ending today (or yesterday, when today has none yet). */
export function streak(calendar:ActivityDay[]){
 let i=calendar.length-1,count=0
 if(i>=0&&!calendar[i].answers)i--
 for(;i>=0&&calendar[i].answers>0;i--)count++
 return count
}

export type Milestone={id:string;title:string;done:boolean;have:number;need:number}
/** Milestones from mastery, letters, checkpoints and practice, easiest first; `have` is capped at `need`. */
export function milestones(records:ProgressRecord[],letterIds:string[],checkpointBests:number[],calendar:ActivityDay[],passMark=.8):Milestone[]{
 const c=masteryCounts(records)
 const known=letterIds.filter(id=>{const m=letterMastery(records,id);return!!m&&m!=='weak'}).length
 const lettersMastered=letterIds.filter(id=>letterMastery(records,id)==='mastered').length
 const answers=records.reduce((t,r)=>t+r.seen,0)
 const m=(id:string,title:string,have:number,need:number):Milestone=>({id,title,done:have>=need,have:Math.min(have,need),need})
 return[
  m('first-mastered','First item mastered',c.mastered,1),
  m('letters-known','All 28 letters practised, none weak',known,letterIds.length),
  m('ten-mastered','10 items mastered',c.mastered,10),
  m('checkpoint','A checkpoint passed',checkpointBests.filter(b=>b>=passMark).length,1),
  m('week-streak','7 days of practice in a row',streak(calendar),7),
  m('letters-mastered','All 28 letters mastered',lettersMastered,letterIds.length),
  m('fifty-mastered','50 items mastered',c.mastered,50),
  m('thousand-answers','1,000 answers',answers,1000),
  m('hundred-mastered','100 items mastered',c.mastered,100),
 ]
}
